import { useState, type ReactNode } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import FormProvider, { RHFAutocomplete, RHFSelect, RHFTextField } from 'src/components/hook-form';
import RHFEditor from 'src/components/hook-form/rhf-editor';
import RHFTextArea from 'src/components/hook-form/rhf-text-area';
import {
  RHFUploadSingleFileAccelerator,
  RHFUploadSingleFileFull,
} from 'src/components/hook-form/rhf-upload';
import RHFRadioGroupLegacy from 'src/components/hook-form/rhf-radio-group-legacy';
import Iconify from 'src/components/iconify';
import { paths } from 'src/routes/paths';
import { useMyDataStore } from 'src/store/me/me.store';
import { LPBenefitType } from 'src/types/lp-benefits';
import BenefitTypeSelector from 'src/modules/benefits/components/benefit-type-selector';
import RHFRadioGroupProduct from '../../coupon-lp-details/components/rhf-radio-group-produc';

function FormSection({
  index,
  title,
  children,
}: {
  index: number;
  title: string;
  children: ReactNode;
}) {
  const [expanded, setExpanded] = useState(true);
  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'common.white',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 700,
              }}
            >
              {index}
            </Box>
            <Typography variant="h6" fontWeight={700}>
              {title}
            </Typography>
          </Stack>
          <IconButton
            aria-label={`${expanded ? 'Recolher' : 'Expandir'} ${title}`}
            onClick={() => setExpanded((value) => !value)}
          >
            <Iconify
              icon={expanded ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
            />
          </IconButton>
        </Stack>
        <Collapse in={expanded}>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {children}
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  );
}

// Retain the original LP hooks for validation, payloads, uploads and approval actions.
type Props = { hook: any; benefitType: LPBenefitType; editDialog: ReactNode };

export default function LPBenefitForm({ hook, benefitType, editDialog }: Props) {
  const navigate = useNavigate();
  const user = useMyDataStore((state) => state.me);
  const isCoupon = benefitType === 'coupon';
  const details = isCoupon ? hook.couponDetails?.coupon : hook.productDetails;
  const approvalStatus = details?.lpEventsData?.approvalStatus;
  const canCreateOrEdit = user?.roles?.some((role) =>
    ['lp_shopkeeper', 'lp_exhibitor', 'lp_contractor'].includes(role)
  );
  const canReview = user?.roles?.some((role) => ['cn_admin', 'lp_promoter'].includes(role));
  const isExhibitor = user?.roles?.includes('lp_exhibitor');
  const disableUpdate = Boolean(
    hook.isEdit &&
    user?.roles?.includes('lp_contractor') &&
    details?.lpEventsData?.creatorId !== user?._id
  );
  const locations = isCoupon ? hook.renderedLocations : hook.renderedLocOptions;
  const label = isCoupon ? 'Cupom' : 'Produto';
  const getTextFromHtml = (html: string) => {
    const element = document.createElement('div');
    element.innerHTML = html || '';
    return element.textContent || element.innerText || '';
  };

  return (
    <FormProvider methods={hook.methods} onSubmit={hook.onSubmit}>
      <Stack spacing={3}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          spacing={2}
          sx={{ p: 1 }}
        >
          <BenefitTypeSelector
            value={benefitType}
            disabled={hook.isEdit}
            onChange={(value) =>
              navigate(`${paths.dashboard.lpBenefitCreate}?benefitType=${value}`)
            }
          />
          <Button color="inherit" onClick={() => navigate(paths.dashboard.lpBenefitsList)}>
            Voltar à listagem
          </Button>
        </Stack>

        {hook.isEdit &&
          canReview &&
          approvalStatus === 'pending' &&
          details?.lpEventsData?.editComment && (
            <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
              <Typography variant="subtitle2">Motivo da Edição</Typography>
              {details.lpEventsData.editComment}
            </Alert>
          )}
        {hook.isEdit && approvalStatus === 'declined' && details?.lpEventsData?.declineComment && (
          <Alert severity="warning" sx={{ whiteSpace: 'pre-wrap' }}>
            <Typography variant="subtitle2">Motivo da Recusa</Typography>
            {details.lpEventsData.declineComment}
          </Alert>
        )}

        <FormSection index={1} title="Exibição">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <RHFSelect name="businessUnitId" label="Unidade de negócio" disabled>
                {hook.businessUnits?.map((option: { value: string; label: string }) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </RHFSelect>
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFSelect name="lpEvent" label="Evento do Viva Visitante" disabled>
                {hook.lpEventsByBusinessUnit?.map((option: { value: string; label: string }) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </RHFSelect>
            </Grid>
          </Grid>
          <RHFAutocomplete
            name="local"
            label="Localização"
            required
            disabled={isExhibitor}
            options={(locations || []).filter(
              (option: { value: string }) => option.value !== 'all'
            )}
          />
          <Grid container spacing={2}>
            {['startDate', 'endDate'].map((name) => (
              <Grid item xs={12} sm={6} key={name}>
                <Controller
                  name={name}
                  control={hook.control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      label={name === 'startDate' ? 'Data de Início' : 'Data de Fim'}
                      minDate={new Date()}
                      closeOnSelect
                      sx={{ width: 1 }}
                      slotProps={{
                        textField: { error: false, helperText: '', inputProps: { readOnly: true } },
                      }}
                    />
                  )}
                />
              </Grid>
            ))}
          </Grid>
          {!hook.isValidEndDate && (
            <Typography variant="caption" color="error">
              Data de término deve ser maior ou igual do que a data de início
            </Typography>
          )}
        </FormSection>

        <FormSection index={2} title="Informações">
          <Stack spacing={1}>
            <FormLabel required sx={{ typography: 'subtitle2', color: 'text.primary' }}>
              Imagem
            </FormLabel>
            {isCoupon ? (
              <RHFUploadSingleFileAccelerator
                name="image"
                acceptAll={false}
                onDrop={hook.handleDrop}
              />
            ) : (
              <RHFUploadSingleFileFull
                name="image"
                acceptAll={false}
                onDrop={hook.handleDrop}
                showPreview={false}
              />
            )}
            <Typography variant="caption" color="text.secondary">
              A imagem deve ter no máximo 800x800 pixels e 600kB
            </Typography>
          </Stack>
          <RHFTextField
            name="name"
            label="Nome"
            inputProps={{ maxLength: 100 }}
            helperText={`${hook.watch('name')?.length || 0}/100 caracteres`}
            onChange={(event) =>
              hook.setValue(
                'name',
                event.target.value.charAt(0).toUpperCase() + event.target.value.slice(1)
              )
            }
          />
          <RHFTextArea name="description" label="Descrição" inputProps={{ maxLength: 300 }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: 'right', mt: '-16px !important' }}
          >
            {hook.watch('description')?.length || 0}/300 caracteres
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <RHFSelect name="category" label="Categoria">
                {hook.categories?.map((option: { value: string; label: string }) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </RHFSelect>
            </Grid>
            <Grid item xs={12} sm={6}>
              <RHFSelect name="theme" label="Tema">
                {hook.themes?.map((option: { value: string; label: string }) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </RHFSelect>
            </Grid>
          </Grid>
          <RHFEditor
            name="rules"
            label="Regras"
            onChange={(value) => {
              if (getTextFromHtml(value).length <= 1000) hook.setValue('rules', value);
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: 'right', mt: '-16px !important' }}
          >
            {getTextFromHtml(hook.watch('rules')).length}/1000 caracteres
          </Typography>
        </FormSection>

        <FormSection index={3} title="Resgate">
          {!isCoupon && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">Tipo de Resgate</Typography>
              <RHFRadioGroupLegacy
                name="rewardType"
                disabled={hook.isEdit}
                options={hook.PRODUCT_TYPE_OPTIONS}
                getOptionLabel={hook.PRODUCT_TYPE_LABEL}
              />
            </Stack>
          )}
          {hook.reward === 'code' && (
            <>
              <Stack spacing={1}>
                <Typography variant="subtitle2">Tipo de exibição dos códigos/links</Typography>
                <RHFRadioGroupProduct
                  name="codeShowType"
                  options={hook.PRODUCT_CODE_OPTIONS}
                  getOptionLabel={hook.PRODUCT_CODE_LABEL}
                />
              </Stack>
              <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={2}>
                <Button variant="soft" onClick={() => hook.setOpen(true)}>
                  Cadastrar Códigos/Links
                </Button>
                {isCoupon && hook.hasCodesToDownload && (
                  <Button
                    variant="soft"
                    onClick={hook.handleDownloadCodes}
                    startIcon={<Iconify icon="solar:download-linear" width={18} />}
                  >
                    Baixar planilha
                  </Button>
                )}
                <Typography variant="overline">
                  {hook.codesEdited
                    ? hook.codes.length
                    : hook.codes.length * hook.redeemCodeQuantity || 0}{' '}
                  Códigos/Links Cadastrados
                </Typography>
              </Stack>
              {!isCoupon && (
                <RHFTextField
                  name="redeemCodeQuantity"
                  type="number"
                  label="Quantidade de códigos/links por resgate"
                  disabled={hook.isEdit}
                />
              )}
            </>
          )}
          {!isCoupon && hook.reward === 'simple' && (
            <RHFTextField name="totalQuantity" type="number" label="Quantidade/Estoque" />
          )}
          <RHFTextField
            name="expirationDays"
            type="number"
            label="Tempo de retirada após resgate (em dias)"
          />
          <RHFTextField
            name="userPurchaseLimit"
            label="Quantidade de resgates por usuário"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            onKeyPress={(event) => {
              if (!/[0-9]/.test(event.key)) event.preventDefault();
            }}
          />
        </FormSection>

        <Stack direction="row" spacing={2} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
          {canCreateOrEdit && (
            <>
              {hook.isEdit && approvalStatus === 'approved' && (
                <LoadingButton
                  variant="contained"
                  disabled={disableUpdate}
                  onClick={hook.handleActivateAndInactivate}
                >
                  {details?.status === 'active' ? 'Inativar' : 'Ativar'} {label}
                </LoadingButton>
              )}
              <LoadingButton
                variant="contained"
                loading={hook.isSubmitting}
                disabled={disableUpdate}
                {...(hook.isEdit
                  ? { onClick: () => hook.setOpenComment(true) }
                  : { type: 'submit' as const })}
              >
                {hook.isEdit ? 'Editar Benefício' : 'Cadastrar Benefício'}
              </LoadingButton>
            </>
          )}
          {canReview && hook.isEdit && (
            <>
              <LoadingButton
                variant="contained"
                disabled={approvalStatus === 'approved'}
                onClick={hook.handleApprove}
              >
                Aprovar {label}
              </LoadingButton>
              <LoadingButton
                variant="contained"
                color="error"
                disabled={approvalStatus === 'declined'}
                onClick={hook.handleRefuse}
              >
                Recusar {label}
              </LoadingButton>
            </>
          )}
        </Stack>
      </Stack>
      {editDialog}
    </FormProvider>
  );
}
